import {useState, useRef} from 'react'
import {Box, CircularProgress, IconButton} from '@mui/material'
import {Microphone} from 'iconsax-reactjs'
import StopCircle from '@mui/icons-material/StopCircle'
import AudioService from 'web-common/services/AudioService'

interface VoiceRecordButtonProps {
  onAudioRecorded: (audio: string) => void
}

const VoiceRecordButton = ({onAudioRecorded}: VoiceRecordButtonProps) => {
  const [recording, setRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [loading, setLoading] = useState(false)

  const audioChunks = useRef<Blob[]>([])

  const startRecording = async () => {
    try {
      const mimeType = 'audio/webm'
      const stream = await navigator.mediaDevices.getUserMedia({audio: true})
      const recorder = new MediaRecorder(stream, {mimeType: mimeType})

      audioChunks.current = []
      recorder.ondataavailable = (e) => {
        audioChunks.current.push(e.data)
      }

      recorder.onstop = async () => {
        const file = new Blob(audioChunks.current, {type: mimeType})
        const formData = new FormData()
        const fileName = 'recording.' + mimeType.split('/')[1]

        formData.append('file', file, fileName)
        setLoading(true)

        AudioService.shared()
          .transcribeAudio(formData)
          .then((result) => {
            onAudioRecorded(result)
          })
          .catch((error) => {
            console.error('Failed to transcribe audio:', error)
          })
          .finally(() => {
            setLoading(false)
          })
      }

      recorder.start()
      setMediaRecorder(recorder)
      setRecording(true)
    } catch (err) {
      console.error('Microphone access denied or not available', err)
    }
  }

  const stopRecording = () => {
    mediaRecorder?.stop()
    setRecording(false)
  }

  return (
    <Box
      sx={{
        width: 'auto',
        height: 'auto',
        marginX: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <IconButton
        size="small"
        color={recording ? 'error' : 'primary'}
        onClick={recording ? stopRecording : startRecording}
        sx={(theme) => ({
          width: 40,
          height: 40,
          padding: 0,
          color: theme.palette.common.black
        })}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : recording ? (
          <StopCircle sx={{fontSize: 32}} />
        ) : (
          <Microphone size={24} />
        )}
      </IconButton>
    </Box>
  )
}

export default VoiceRecordButton
